import { z } from 'zod';

import {
  COMPLIANCE_CADENCES,
  COMPLIANCE_LOG_OUTCOMES,
  COMPLIANCE_RECORD_STATUSES,
} from './compliance-constants.js';
import { cursorPaginationQuery } from './pagination.js';

/**
 * Phase 5 — compliance request/response schemas. Every endpoint that
 * accepts user input parses through one of these (via the
 * `validate({ body | query | params })` middleware).
 */

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v.length === 0 ? null : v))
    .nullish()
    .transform((v) => v ?? null);

const metadata = z
  .record(z.union([z.string(), z.number(), z.boolean(), z.null()]))
  .optional()
  .default({});

function csvList<T extends z.ZodTypeAny>(item: T) {
  return z
    .union([z.array(item), z.string()])
    .optional()
    .transform((v) => {
      if (v === undefined) return undefined;
      if (Array.isArray(v)) return v;
      const parts = v
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      return parts as unknown as z.infer<T>[];
    })
    .pipe(z.array(item).optional());
}

// ---------- device types ----------

export const createDeviceTypeInput = z
  .object({
    name: z.string().trim().min(1).max(120),
    slug: z
      .string()
      .trim()
      .min(1)
      .max(80)
      .regex(/^[a-z0-9][a-z0-9-]*[a-z0-9]$/, 'Use lowercase letters, digits, and hyphens'),
    isComplianceTracked: z.boolean().default(true),
    defaultCadence: z.enum(COMPLIANCE_CADENCES).nullish(),
    defaultCustomIntervalDays: z.number().int().positive().max(3_650).nullish(),
    defaultGracePeriodDays: z.number().int().min(0).max(365).default(0),
    requiresUnit: z.boolean().default(false),
    icon: optionalText(80),
    regulatoryNotes: optionalText(2_000),
    metadata,
  })
  .superRefine((v, ctx) => {
    if (v.isComplianceTracked && !v.defaultCadence) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['defaultCadence'],
        message: 'Required when isComplianceTracked is true',
      });
    }
    if (!v.isComplianceTracked && v.defaultCadence) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['defaultCadence'],
        message: 'Must be null when isComplianceTracked is false',
      });
    }
    if (v.defaultCadence === 'custom' && !v.defaultCustomIntervalDays) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['defaultCustomIntervalDays'],
        message: 'Required when defaultCadence is "custom"',
      });
    }
  });
export type CreateDeviceTypeInput = z.infer<typeof createDeviceTypeInput>;

export const updateDeviceTypeInput = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    isComplianceTracked: z.boolean().optional(),
    defaultCadence: z.enum(COMPLIANCE_CADENCES).nullish().optional(),
    defaultCustomIntervalDays: z.number().int().positive().max(3_650).nullish().optional(),
    defaultGracePeriodDays: z.number().int().min(0).max(365).optional(),
    requiresUnit: z.boolean().optional(),
    icon: optionalText(80).optional(),
    regulatoryNotes: optionalText(2_000).optional(),
    isActive: z.boolean().optional(),
    metadata: metadata.optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'Provide at least one field to update',
  });
export type UpdateDeviceTypeInput = z.infer<typeof updateDeviceTypeInput>;

export const deviceTypeFiltersQuery = cursorPaginationQuery.extend({
  includeInactive: z.coerce.boolean().optional().default(false),
  isComplianceTracked: z.coerce.boolean().optional(),
  q: z.string().trim().max(120).optional(),
});
export type DeviceTypeFiltersQuery = z.infer<typeof deviceTypeFiltersQuery>;

// ---------- devices ----------

export const createDeviceInput = z.object({
  buildingId: z.string().uuid(),
  unitId: z.string().uuid().nullish().transform((v) => v ?? null),
  deviceTypeId: z.string().uuid(),
  label: z.string().trim().min(1).max(200),
  make: optionalText(120),
  model: optionalText(120),
  serialNumber: optionalText(120),
  installedAt: z.string().date().nullish().transform((v) => v ?? null),
  warrantyExpiresOn: z.string().date().nullish().transform((v) => v ?? null),
  purchasePrice: z.number().nonnegative().nullish().transform((v) => v ?? null),
  purchaseVendor: optionalText(200),
  cadenceOverride: z.enum(COMPLIANCE_CADENCES).nullish().transform((v) => v ?? null),
  customIntervalDaysOverride: z.number().int().positive().max(3_650).nullish().transform((v) => v ?? null),
  gracePeriodDaysOverride: z.number().int().min(0).max(365).nullish().transform((v) => v ?? null),
  isComplianceTrackedOverride: z.boolean().nullish().transform((v) => v ?? null),
  notes: optionalText(8_000),
  metadata,
});
export type CreateDeviceInput = z.infer<typeof createDeviceInput>;

export const updateDeviceInput = z
  .object({
    unitId: z.string().uuid().nullish().transform((v) => v ?? null).optional(),
    label: z.string().trim().min(1).max(200).optional(),
    make: optionalText(120).optional(),
    model: optionalText(120).optional(),
    serialNumber: optionalText(120).optional(),
    installedAt: z.string().date().nullish().transform((v) => v ?? null).optional(),
    warrantyExpiresOn: z.string().date().nullish().transform((v) => v ?? null).optional(),
    purchasePrice: z.number().nonnegative().nullish().transform((v) => v ?? null).optional(),
    purchaseVendor: optionalText(200).optional(),
    cadenceOverride: z.enum(COMPLIANCE_CADENCES).nullish().transform((v) => v ?? null).optional(),
    customIntervalDaysOverride: z.number().int().positive().max(3_650).nullish().transform((v) => v ?? null).optional(),
    gracePeriodDaysOverride: z.number().int().min(0).max(365).nullish().transform((v) => v ?? null).optional(),
    isComplianceTrackedOverride: z.boolean().nullish().transform((v) => v ?? null).optional(),
    notes: optionalText(8_000).optional(),
    isActive: z.boolean().optional(),
    metadata: metadata.optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'Provide at least one field to update',
  });
export type UpdateDeviceInput = z.infer<typeof updateDeviceInput>;

export const convertDeviceToTrackedInput = z
  .object({
    cadenceOverride: z.enum(COMPLIANCE_CADENCES),
    customIntervalDaysOverride: z.number().int().positive().max(3_650).nullish().transform((v) => v ?? null),
    gracePeriodDaysOverride: z.number().int().min(0).max(365).nullish().transform((v) => v ?? null),
  })
  .superRefine((v, ctx) => {
    if (v.cadenceOverride === 'custom' && !v.customIntervalDaysOverride) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['customIntervalDaysOverride'],
        message: 'Required when cadenceOverride is "custom"',
      });
    }
  });
export type ConvertDeviceToTrackedInput = z.infer<typeof convertDeviceToTrackedInput>;

export const bulkCreateDevicesInput = z.object({
  buildingId: z.string().uuid(),
  deviceTypeId: z.string().uuid(),
  /** Per-unit device specs. The API derives the label from `unitNumber`
   *  if not supplied. Must include `unitId` if the device type requires
   *  a unit; the API enforces this. */
  devices: z
    .array(
      z.object({
        unitId: z.string().uuid().nullish().transform((v) => v ?? null),
        label: z.string().trim().min(1).max(200).optional(),
        serialNumber: optionalText(120).optional(),
      }),
    )
    .min(1)
    .max(500),
});
export type BulkCreateDevicesInput = z.infer<typeof bulkCreateDevicesInput>;

export const deviceFiltersQuery = cursorPaginationQuery.extend({
  buildingId: z.string().uuid().optional(),
  unitId: z.string().uuid().optional(),
  deviceTypeId: z.string().uuid().optional(),
  isComplianceTracked: z.coerce.boolean().optional(),
  includeInactive: z.coerce.boolean().optional().default(false),
  dueWithinDays: z.coerce.number().int().min(0).max(365).optional(),
  q: z.string().trim().max(120).optional(),
  sort: z
    .enum(['next_due_at', '-next_due_at', 'label', '-created_at'])
    .optional()
    .default('next_due_at'),
});
export type DeviceFiltersQuery = z.infer<typeof deviceFiltersQuery>;

// ---------- compliance records ----------

export const complianceRecordFiltersQuery = cursorPaginationQuery.extend({
  status: csvList(z.enum(COMPLIANCE_RECORD_STATUSES)),
  buildingId: z.string().uuid().optional(),
  deviceTypeId: z.string().uuid().optional(),
  dueBefore: z.string().datetime({ offset: true }).optional(),
  dueAfter: z.string().datetime({ offset: true }).optional(),
  q: z.string().trim().max(120).optional(),
  sort: z
    .enum(['due_at', '-due_at', '-updated_at'])
    .optional()
    .default('due_at'),
});
export type ComplianceRecordFiltersQuery = z.infer<typeof complianceRecordFiltersQuery>;

export const submitComplianceLogInput = z.object({
  performedOn: z.string().date(),
  outcome: z.enum(COMPLIANCE_LOG_OUTCOMES),
  notes: optionalText(8_000),
  attachmentIds: z.array(z.string().uuid()).max(50).optional().default([]),
  signatureAttachmentId: z.string().uuid().nullish().transform((v) => v ?? null),
  nextDueOverride: z.string().datetime({ offset: true }).nullish().transform((v) => v ?? null),
  /** Marks the log as performed by a vendor user (Phase 3 sign-off). */
  loggedByVendorUserId: z.string().uuid().nullish().transform((v) => v ?? null),
  metadata,
});
export type SubmitComplianceLogInput = z.infer<typeof submitComplianceLogInput>;

export const skipComplianceRecordInput = z.object({
  reason: z.string().trim().min(1).max(2_000),
});
export type SkipComplianceRecordInput = z.infer<typeof skipComplianceRecordInput>;

export const snoozeComplianceRecordInput = z.object({
  days: z.number().int().min(1).max(365),
  reason: z.string().trim().min(1).max(2_000),
});
export type SnoozeComplianceRecordInput = z.infer<typeof snoozeComplianceRecordInput>;

// ---------- attachments (polymorphic owner) ----------

/** Presign kinds for the compliance attachment flow. The API accepts
 *  these in addition to the work-order kinds. */
export const COMPLIANCE_ATTACHMENT_KINDS = [
  'compliance_log_photo',
  'compliance_log_document',
  'compliance_log_signature',
  'device_document',
] as const;
export type ComplianceAttachmentKind = (typeof COMPLIANCE_ATTACHMENT_KINDS)[number];

export const complianceAttachmentPresignInput = z.object({
  kind: z.enum(COMPLIANCE_ATTACHMENT_KINDS),
  filename: z.string().trim().min(1).max(255),
  contentType: z
    .string()
    .trim()
    .min(1)
    .max(120)
    .regex(/^[\w.+-]+\/[\w.+-]+$/, 'Invalid content type'),
  sizeBytes: z.number().int().positive().max(50 * 1024 * 1024),
});
export type ComplianceAttachmentPresignInput = z.infer<typeof complianceAttachmentPresignInput>;
